import type { PlanInfo } from "schemas/billing";
import type { BillingTier } from "schemas/generated/billing";

import { format } from "date-fns";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { z } from "zod";

export type CancellationStep = "reasons" | "retention" | "confirmation";

export const CANCELLATION_REASONS = [
  { value: "store-closing", label: "I'm closing my store" },
  { value: "too-expensive", label: "It's too expensive" },
  { value: "missing-features", label: "It's missing features I need" },
  { value: "hard-to-learn", label: "It was hard to learn" },
  { value: "found-another-tool", label: "I found another tool" },
  { value: "support-not-helpful", label: "Support wasn't helpful" },
  { value: "not-using-it-enough", label: "I'm not using it enough" },
  { value: "something-else", label: "Something else" },
] as const;

export type CancellationReason = (typeof CANCELLATION_REASONS)[number]["value"];

const CANCELLATION_REASON_VALUES = CANCELLATION_REASONS.map(
  (reason) => reason.value,
);

/** The one reason that carries no information on its own, so the customer has to write it down. */
const OPEN_ENDED_CANCELLATION_REASON: CancellationReason = "something-else";

export const CANCELLATION_FEEDBACK_MAX_LENGTH = 5000;
const CANCELLATION_FEEDBACK_MIN_LENGTH = 2;

export function isCancellationFeedbackRequired(
  reason: CancellationReason | null | undefined,
): boolean {
  return reason === OPEN_ENDED_CANCELLATION_REASON;
}

export function getCancellationFeedbackField(
  reason: CancellationReason | null | undefined,
): { label: string; placeholder: string; isRequired: boolean } {
  return isCancellationFeedbackRequired(reason)
    ? {
        label: "Tell us why you're cancelling",
        placeholder: "A sentence or two is plenty.",
        isRequired: true,
      }
    : {
        label: "Anything else? (optional)",
        placeholder: "What would have made you stay?",
        isRequired: false,
      };
}

export const cancellationFormSchema = z
  .object({
    reason: z.enum(CANCELLATION_REASON_VALUES, {
      error: "Choose a reason to continue",
    }),
    feedback: z
      .string()
      .max(
        CANCELLATION_FEEDBACK_MAX_LENGTH,
        `Keep this under ${CANCELLATION_FEEDBACK_MAX_LENGTH.toLocaleString()} characters`,
      ),
  })
  .superRefine(({ reason, feedback }, ctx) => {
    if (
      isCancellationFeedbackRequired(reason) &&
      feedback.trim().length < CANCELLATION_FEEDBACK_MIN_LENGTH
    ) {
      ctx.addIssue({
        path: ["feedback"],
        code: "custom",
        message:
          "Please provide some detail on why you're cancelling. Even a few words really helps!",
      });
    }
  });

export type CancellationFormValues = z.infer<typeof cancellationFormSchema>;

export const CANCELLATION_FORM_DEFAULT_VALUES = {
  reason: undefined,
  feedback: "",
} as const;

export type CancellationRetentionOffer =
  | "agentFreeMonth"
  | "starterDowngrade"
  | "agent"
  | null;

export type CancellationSubmissionLock = {
  isSubmitting: boolean;
};

const starterKeepDefinitions: {
  describe: (maximum: number | null) => string;
  getMaximum: (plan: PlanInfo) => number | null;
}[] = [
  {
    describe: (maximum) => {
      return maximum === null
        ? "Unlimited published items on Shopify"
        : `Up to ${maximum.toLocaleString()} published items on Shopify`;
    },
    getMaximum: (plan) => plan.maxPublishedElements,
  },
  {
    describe: (maximum) => {
      return maximum === 1
        ? "Your Theme Builder project"
        : `${formatAllowance(maximum)} Theme Builder projects`;
    },
    getMaximum: (plan) => plan.maxShopifyThemeProjects,
  },
  {
    describe: (maximum) => `${formatAllowance(maximum)} custom domains`,
    getMaximum: (plan) => plan.maxCustomDomains,
  },
  {
    describe: (maximum) => `${formatAllowance(maximum)} A/B tests`,
    getMaximum: (plan) => plan.maxExperiments,
  },
];

function formatAllowance(maximum: number | null): string {
  return maximum === null ? "Unlimited" : maximum.toLocaleString();
}

// Only entitlements Free strips, so the offer lists what Starter actually preserves.
export function getStarterKeeps({
  starterPlan,
  freePlan,
}: {
  starterPlan: PlanInfo;
  freePlan: PlanInfo;
}): string[] {
  return starterKeepDefinitions.flatMap((definition) => {
    const starterMaximum = definition.getMaximum(starterPlan);
    const freeMaximum = definition.getMaximum(freePlan);
    const keepsMore =
      freeMaximum !== null &&
      (starterMaximum === null || starterMaximum > freeMaximum);
    return keepsMore ? [definition.describe(starterMaximum)] : [];
  });
}

// Mirrors the plan picker: unknown usage is treated as fitting, so a slow query never hides the offer.
export function isUsageWithinPlan({
  plan,
  usage,
}: {
  plan: PlanInfo;
  usage: { publishedElements?: number; themeProjects?: number };
}): boolean {
  const fits = (current: number | undefined, maximum: number | null) => {
    return current === undefined || maximum === null || current <= maximum;
  };
  return (
    fits(usage.publishedElements, plan.maxPublishedElements) &&
    fits(usage.themeProjects, plan.maxShopifyThemeProjects)
  );
}

export function formatCancellationEndDate(
  nextBillingDate: Date | string | null | undefined,
): string {
  const endOfBillingPeriod = "the end of your billing period";

  if (!nextBillingDate) {
    return endOfBillingPeriod;
  }

  const parsedBillingDate = new Date(nextBillingDate);
  if (Number.isNaN(parsedBillingDate.getTime())) {
    return endOfBillingPeriod;
  }

  return format(parsedBillingDate, "MMMM d, yyyy");
}

export function getCancellationRetentionOffer({
  currentTier,
  reason,
  isAgentFreeMonthAvailable = false,
  isAgentAvailable,
  isDowngradeAvailable,
}: {
  currentTier: BillingTier;
  reason: CancellationReason | null;
  isAgentFreeMonthAvailable?: boolean;
  isAgentAvailable: boolean;
  /** Plan changes on an existing subscription are Shopify-only server-side. */
  isDowngradeAvailable: boolean;
}): CancellationRetentionOffer {
  if (reason === null) {
    return null;
  }

  if (isAgentFreeMonthAvailable) {
    return "agentFreeMonth";
  }

  if (isAgentAvailable) {
    return "agent";
  }

  if (
    isDowngradeAvailable &&
    currentTier === "pro" &&
    reason === "too-expensive"
  ) {
    return "starterDowngrade";
  }

  return null;
}

export function getNextCancellationStep({
  currentStep,
  retentionOffer,
}: {
  currentStep: CancellationStep;
  retentionOffer: CancellationRetentionOffer;
}): CancellationStep | null {
  return exhaustiveSwitch(currentStep)({
    reasons: (): CancellationStep | null => {
      return retentionOffer === null ? "confirmation" : "retention";
    },
    retention: (): CancellationStep | null => "confirmation",
    confirmation: (): CancellationStep | null => null,
  });
}

export async function submitCancellationOnce({
  submissionLock,
  submit,
}: {
  submissionLock: CancellationSubmissionLock;
  submit: () => Promise<void>;
}): Promise<boolean> {
  if (submissionLock.isSubmitting) {
    return false;
  }

  submissionLock.isSubmitting = true;
  try {
    await submit();
    return true;
  } finally {
    submissionLock.isSubmitting = false;
  }
}
